'use client'

import { useEffect } from 'react'
import {
    beginDwellSession,
    endDwellSession,
    flushDwellSession,
    setPageFocus,
    setPageVisibility,
} from '@/lib/analytics/landingPageDwell'
import { clearPageViewId } from '@/lib/analytics/pageViewId'
import { startObserverLifecycle, stopObserverLifecycle } from './dwellObserver'

interface LandingPageDwellTrackerProps {
    pageId: string
    skuId?: string | null
}

/** Owns the dwell-session lifecycle for one landing-page nav.
 *  Pagehide is the only flush trigger; visibility changes pause/resume
 *  accumulation but never flush (flushing on hidden + the
 *  one-flush-per-session guard would silently drop post-return data
 *  if the user tab-switched and came back). */
export default function LandingPageDwellTracker({ pageId, skuId }: LandingPageDwellTrackerProps) {
    useEffect(() => {
        beginDwellSession({ pageId, skuId: skuId ?? null })
        startObserverLifecycle()

        const onVisibilityChange = () => {
            setPageVisibility(document.visibilityState === 'visible')
        }
        const onFocus = () => setPageFocus(true)
        const onBlur = () => setPageFocus(false)
        // Flush BEFORE clearing — the flush needs the current page_view_id
        // to correlate with landing_page_loaded + visit_options_preview_shown.
        // Skip clear on bfcache (event.persisted) so back/forward stays one pageview.
        const onPageHide = (e: PageTransitionEvent) => {
            flushDwellSession('page_hide')
            if (!e.persisted) clearPageViewId()
        }

        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('focus', onFocus)
        window.addEventListener('blur', onBlur)
        window.addEventListener('pagehide', onPageHide)

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('focus', onFocus)
            window.removeEventListener('blur', onBlur)
            window.removeEventListener('pagehide', onPageHide)
            // Flush on unmount covers SPA route changes that skip pagehide.
            flushDwellSession('unload')
            stopObserverLifecycle()
            endDwellSession()
        }
    }, [pageId, skuId])

    return null
}
